import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'

@customElement('primary-subscriber-form')
export class PrimarySubscriberForm extends LitElement {
  static styles = css`
    .form-card-border {
      border-radius: var(--Radii-md, 12px);
      border: 1px solid var(--Form-input-border-color, #d1d3d4);
      box-shadow: none;
    }

    /* Force white background for Coverage start date select */
    #primary-subscriber-start-date {
      background-color: #ffffff;
      /* Try design system var if supported */
      --Form-input-background-color: #ffffff;
      --Form-input-error-background-color: #ffffff;
    }
    /* Cover common shadow parts used by select components */
    #primary-subscriber-start-date::part(base),
    #primary-subscriber-start-date::part(control),
    #primary-subscriber-start-date::part(select),
    #primary-subscriber-start-date::part(input),
    #primary-subscriber-start-date::part(trigger) {
      background-color: #ffffff !important;
    }
  `

  @property({ type: Array })
  effectiveDateOptions: string[] = []

  @property({ type: String })
  dob: string = ''

  @property({ type: String })
  startDate: string = ''

  @property({ type: Boolean })
  emailRequired: boolean = false

  @property({ type: Boolean })
  phoneRequired: boolean = false

  @property({ type: Boolean })
  isAgent: boolean = false

  @property({ type: Boolean })
  isEmbedded: boolean = false

  @property({ type: String })
  firstName: string = ''

  @property({ type: String })
  lastName: string = ''

  @property({ type: String })
  email: string = ''

  @property({ type: String })
  phone: string = ''

  @state()
  private isOverAge = false

  @state()
  private hasFirstNameError = false

  @state()
  private hasLastNameError = false

  private selectBgObserver?: MutationObserver

  // Pattern to validate name fields: only letters, spaces, hyphens, apostrophes, and periods
  private namePattern = /^[A-Za-z\-'.\s]+$/

  private validateNamePattern(value: string): boolean {
    if (!value) return false
    return !this.namePattern.test(value)
  }

  // Computed display value for the DOB in MM/DD/YYYY
  get formattedDob(): string {
    return this.formatDateToMMDDYYYY(this.dob)
  }

  private formatDateToMMDDYYYY(date: string): string {
    // Convert from YYYY-MM-DD to MM/DD/YYYY
    if (!date) return ''
    if (date.includes('/')) return date // Already in correct format

    const parts = date.split('-')
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`
    }
    return date
  }

  private parseMMDDYYYY(dateStr: string): Date | null {
    if (!dateStr) return null
    const normalized = dateStr.includes('/')
      ? dateStr
      : this.formatDateToMMDDYYYY(dateStr)
    const parts = normalized.split('/')
    if (parts.length !== 3) return null
    const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10))
    if (!mm || !dd || !yyyy) return null
    return new Date(yyyy, mm - 1, dd)
  }

  private calcAgeOnDate(dobStr: string, onDateStr: string): number | null {
    const dob = this.parseMMDDYYYY(dobStr)
    const onDate = this.parseMMDDYYYY(onDateStr)
    if (!dob || !onDate) return null
    let age = onDate.getFullYear() - dob.getFullYear()
    const m = onDate.getMonth() - dob.getMonth()
    if (m < 0 || (m === 0 && onDate.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  private updateAgeFlag(dobStr: string) {
    if (!dobStr) {
      this.isOverAge = false
      return
    }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const yyyy = String(today.getFullYear())
    const todayStr = `${mm}/${dd}/${yyyy}`
    const age = this.calcAgeOnDate(dobStr, todayStr)
    // Check if date is in the future or age is over 120
    const dob = this.parseMMDDYYYY(dobStr)
    const dobIsInFuture = dob && dob > today
    this.isOverAge = dobIsInFuture || (age ?? 0) > 120
    this.requestUpdate()
  }

  updated(changedProps: Map<string, any>) {
    super.updated?.(changedProps)
    // Validate firstName when it changes
    if (changedProps.has('firstName')) {
      this.hasFirstNameError = this.validateNamePattern(this.firstName)
      this.dispatchEvent(
        new CustomEvent('first-name-change', {
          detail: { value: this.firstName, hasError: this.hasFirstNameError },
          bubbles: true,
          composed: true,
        })
      )
    }
    // Validate lastName when it changes
    if (changedProps.has('lastName')) {
      this.hasLastNameError = this.validateNamePattern(this.lastName)
      this.dispatchEvent(
        new CustomEvent('last-name-change', {
          detail: { value: this.lastName, hasError: this.hasLastNameError },
          bubbles: true,
          composed: true,
        })
      )
    }
    // const formattedDob = this.formatDateToMMDDYYYY(this.dob)
    // console.log('DOB:', { original: this.dob, formatted: formattedDob })

    // if (changedProps.has('dob') && formattedDob) {
    //   const dobElement = document.getElementById(
    //     'primary-subscriber-dob'
    //   ) as any
    //   if (dobElement) {
    //     dobElement.value = formattedDob
    //   }
    // }
    // Manually update the input values in shadow DOM when props change
    //if (changedProps.has('dob') && this.dob) {
    //   document.getElementById('primary-subscriber-dob')?.change(this.dob)
    // this.updateComplete.then(() => {
    //   setTimeout(() => {
    //     const dobInput = this.shadowRoot?.querySelector('[name="dob"]') as any
    //     console.log('Trying to set DOB:', { dobInput, value: this.dob })
    //     if (dobInput) {
    //       dobInput.value = this.dob
    //       dobInput.setAttribute('value', this.dob)
    //       // Try to dispatch a change event
    //       dobInput.dispatchEvent(new Event('change', { bubbles: true }))
    //     }
    //   }, 100)
    // })
    // }
    if (changedProps.has('startDate') && this.startDate) {
      const startDateElement = this.shadowRoot?.getElementById(
        'primary-subscriber-start-date'
      ) as any
      startDateElement?.input(this.startDate)
    }
    // lh-input type="dob" default-value doesn't apply masking; use change() after render
    if (changedProps.has('dob') && this.dob) {
      const storedDob = this.dob
      setTimeout(() => {
        if (!this.isConnected) return
        const dobInput = this.shadowRoot?.querySelector(
          'lh-input[name="dob"]'
        ) as {
          change?: (value: string) => void
        } | null
        const iso = storedDob.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        dobInput?.change?.(iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : storedDob)
      }, 0)
    }
    // lh-select.setOptionEvents() only runs at connectedCallback and misses
    // options added later by repeat(); re-run it when the list changes.
    if (changedProps.has('effectiveDateOptions')) {
      this.updateComplete.then(() => {
        const sel = this.shadowRoot?.getElementById(
          'primary-subscriber-start-date'
        ) as any
        sel?.setOptionEvents?.()
      })
    }
  }

  private getDobErrorMessage(): string {
    if (!this.isOverAge) return ''
    const dob = this.parseMMDDYYYY(this.dob)
    const today = new Date()
    if (dob && dob > today) {
      return 'Date of birth cannot be in the future.'
    }
    return 'Must be a valid date of birth (age 18-120).'
  }

  private handleDobChange(event: CustomEvent) {
    const dobValue = (event as any).detail?.value || ''
    this.updateAgeFlag(dobValue)
    this.dispatchEvent(
      new CustomEvent('dob-change', {
        detail: { value: dobValue, overAge: this.isOverAge },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleStartDateChange(event: CustomEvent) {
    const value = (event as any).detail?.value || ''
    this.startDate = value
    this.dispatchEvent(
      new CustomEvent('start-date-change', {
        detail: { value },
        bubbles: true,
        composed: true,
      })
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('lh-change', this.handleChange as EventListener)
    this.addEventListener('lh-input', this.handleInput as EventListener)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('lh-change', this.handleChange as EventListener)
    this.removeEventListener('lh-input', this.handleInput as EventListener)
    this.selectBgObserver?.disconnect()
    this.selectBgObserver = undefined
  }

  private handleInput(event: CustomEvent) {
    const path = event.composedPath()
    const input = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-input'
    ) as HTMLElement

    // Only handle firstName and lastName on input events (real-time validation)
    if (input && input.getAttribute('name') === 'firstName') {
      const value = (event as any).detail?.value || ''
      this.hasFirstNameError = this.validateNamePattern(value)
      this.dispatchEvent(
        new CustomEvent('first-name-change', {
          detail: { value, hasError: this.hasFirstNameError },
          bubbles: true,
          composed: true,
        })
      )
    }

    if (input && input.getAttribute('name') === 'lastName') {
      const value = (event as any).detail?.value || ''
      this.hasLastNameError = this.validateNamePattern(value)
      this.dispatchEvent(
        new CustomEvent('last-name-change', {
          detail: { value, hasError: this.hasLastNameError },
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  private handleChange(event: CustomEvent) {
    const path = event.composedPath()
    const input = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-input'
    ) as HTMLElement

    if (input && input.getAttribute('name') === 'dob') {
      this.handleDobChange(event)
    }

    if (input && input.getAttribute('name') === 'firstName') {
      const value = (event as any).detail?.value || ''
      this.hasFirstNameError = this.validateNamePattern(value)
      this.dispatchEvent(
        new CustomEvent('first-name-change', {
          detail: { value, hasError: this.hasFirstNameError },
          bubbles: true,
          composed: true,
        })
      )
    }

    if (input && input.getAttribute('name') === 'lastName') {
      const value = (event as any).detail?.value || ''
      this.hasLastNameError = this.validateNamePattern(value)
      this.dispatchEvent(
        new CustomEvent('last-name-change', {
          detail: { value, hasError: this.hasLastNameError },
          bubbles: true,
          composed: true,
        })
      )
    }

    if (input && input.getAttribute('name') === 'email') {
      this.dispatchEvent(
        new CustomEvent('email-change', {
          detail: { value: (event as any).detail?.value },
          bubbles: true,
          composed: true,
        })
      )
    }

    if (input && input.getAttribute('name') === 'phone') {
      this.dispatchEvent(
        new CustomEvent('phone-change', {
          detail: { value: (event as any).detail?.value },
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  firstUpdated() {
    // Ensure the select's control area renders with white background
    this.ensureStartDateSelectBgWhite()
  }

  private ensureStartDateSelectBgWhite() {
    const selectEl = this.shadowRoot?.getElementById(
      'primary-subscriber-start-date'
    ) as (HTMLElement & { shadowRoot?: ShadowRoot }) | null

    const apply = () => {
      const control = selectEl?.shadowRoot?.querySelector(
        '.lh-select__control'
      ) as HTMLElement | null
      const content = selectEl?.shadowRoot?.querySelector(
        '.lh-select__content'
      ) as HTMLElement | null
      if (control) control.style.backgroundColor = '#ffffff'
      if (content) content.style.backgroundColor = '#ffffff'
    }

    if (!selectEl) return

    // Apply immediately (or after upgrade)
    if (!selectEl.shadowRoot) {
      setTimeout(apply, 0)
    } else {
      apply()
    }

    // Observe internal changes and re-apply to keep white even in error state
    if (selectEl.shadowRoot && !this.selectBgObserver) {
      this.selectBgObserver = new MutationObserver(() => {
        apply()
      })
      this.selectBgObserver.observe(selectEl.shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    }
  }

  render() {
    return html`
      <lh-grid>
        <lh-grid-item>
          <lh-container
            spacing="none"
            gutters="none"
            context-id="primary-subscriber-form"
          >
            <lh-card class="form-card-border">
              <lh-image></lh-image>
              <div slot="content">
                <form id="my-form" name="my-form">
                  <lh-typography size="text-lg">
                    <strong>Primary subscriber</strong>
                  </lh-typography>
                  ${this.isAgent || this.isEmbedded
                    ? html`<lh-grid columns="2" gap="md">
                        <lh-grid-item
                          ><lh-input
                            name="firstName"
                            type="text"
                            required
                            max-count="40"
                            error-message=${this.hasFirstNameError
                              ? "Only letters, spaces, hyphen (-), apostrophe ('), and period (.) are allowed."
                              : ''}
                            default-value=${this.firstName}
                            >First Name</lh-input
                          ></lh-grid-item
                        >
                        <lh-grid-item
                          ><lh-input
                            name="lastName"
                            type="text"
                            required
                            max-count="40"
                            error-message=${this.hasLastNameError
                              ? "Only letters, spaces, hyphen (-), apostrophe ('), and period (.) are allowed."
                              : ''}
                            default-value=${this.lastName}
                            >Last Name</lh-input
                          ></lh-grid-item
                        >
                      </lh-grid>`
                    : nothing}
                  <lh-grid columns="2" gap="md">
                    <lh-grid-item>
                      <lh-input
                        name="dob"
                        type="dob"
                        id="primary-subscriber-dob"
                        data-testid="primary-dob"
                        default-value=${this.formattedDob}
                        error-message=${this.getDobErrorMessage()}
                        required
                      >
                        Date of birth
                      </lh-input>
                    </lh-grid-item>
                    <lh-grid-item>
                      <lh-select
                        required
                        id="primary-subscriber-start-date"
                        data-testid="start-date"
                        help-text=" "
                        @lh-change=${this.handleStartDateChange}
                      >
                        Coverage start date
                        <lh-option
                          slot="options"
                          value
                          ?default-selected=${!this.startDate}
                        >
                          Select Start Date
                        </lh-option>
                        ${repeat(
                          this.effectiveDateOptions,
                          (date) => date,
                          (date) => html`
                            <lh-option
                              slot="options"
                              value=${date}
                              ?default-selected=${date === this.startDate}
                            >
                              ${date}
                            </lh-option>
                          `
                        )}
                      </lh-select>
                    </lh-grid-item>
                  </lh-grid>
                  ${this.isAgent || this.isEmbedded
                    ? html`<lh-grid columns="2" gap="md">
                        <lh-grid-item
                          ><lh-input
                            name="email"
                            type="email"
                            ?required=${this.emailRequired}
                            default-value=${this.email}
                            >Email</lh-input
                          ></lh-grid-item
                        >
                        <lh-grid-item
                          ><lh-input
                            name="phone"
                            type="tel"
                            ?required=${this.phoneRequired}
                            default-value=${this.phone}
                            >Phone</lh-input
                          ></lh-grid-item
                        >
                      </lh-grid>`
                    : nothing}
                </form>
              </div>
            </lh-card>
          </lh-container>
        </lh-grid-item>
      </lh-grid>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'primary-subscriber-form': PrimarySubscriberForm
  }
}
